import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import config from '../config';

const emptyFaq = { category: 'General', question: '', answer: '', sort_order: 0, is_active: true };

const editorModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const errorMessage = async (response, fallback) => {
  try {
    const body = await response.json();
    return body.message || body.error || fallback;
  } catch {
    return fallback;
  }
};

const MobileContentPage = () => {
  const { auth } = useAuth();
  const [activeSection, setActiveSection] = useState('terms');
  const isBusinessSection = activeSection === 'business_terms' || activeSection === 'business_privacy';
  const [pages, setPages] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [faqForm, setFaqForm] = useState(emptyFaq);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewTheme, setPreviewTheme] = useState('light');
  const [faqFilterCategory, setFaqFilterCategory] = useState('all');
  const [faqSearchQuery, setFaqSearchQuery] = useState('');

  const loadContent = useCallback(async () => {
    setLoading(true);
    try {
      const [pagesResponse, faqsResponse] = await Promise.all([
        auth.fetch(`${config.API_BASE_URL}/api/admin/content/pages`),
        auth.fetch(`${config.API_BASE_URL}/api/admin/content/faqs`)
      ]);
      if (!pagesResponse.ok) throw new Error(await errorMessage(pagesResponse, 'Could not load content pages.'));
      if (!faqsResponse.ok) throw new Error(await errorMessage(faqsResponse, 'Could not load FAQs.'));

      const pagesBody = await pagesResponse.json();
      const faqsBody = await faqsResponse.json();
      setPages(Object.fromEntries((pagesBody.pages || []).map(page => [page.slug, {
        ...page,
        effective_date: toDateInput(page.effective_date),
        is_published: Boolean(page.is_published)
      }])));
      setFaqs(faqsBody.faqs || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const currentPage = pages[activeSection];
  const aboutData = useMemo(() => {
    try {
      const parsed = JSON.parse(pages.about?.content || '{}');
      return {
        ...parsed,
        social: parsed.social || {},
        features: Array.isArray(parsed.features) ? parsed.features : []
      };
    } catch {
      return { social: {}, features: [] };
    }
  }, [pages.about?.content]);

  const updateCurrentPage = (field, value) => {
    setPages(previous => ({
      ...previous,
      [activeSection]: { ...previous[activeSection], [field]: value }
    }));
  };

  const updateAbout = (updater) => {
    setPages(previous => {
      let current = {};
      try {
        current = JSON.parse(previous.about?.content || '{}');
      } catch {
        current = {};
      }
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      return {
        ...previous,
        about: { ...previous.about, content: JSON.stringify(next) }
      };
    });
  };

  const updateAboutField = (field, value) => updateAbout(current => ({ ...current, [field]: value }));
  const updateAboutSocial = (field, value) => updateAbout(current => ({
    ...current,
    social: { ...(current.social || {}), [field]: value }
  }));
  const updateAboutFeature = (index, field, value) => updateAbout(current => ({
    ...current,
    features: (current.features || []).map((feature, featureIndex) =>
      featureIndex === index ? { ...feature, [field]: value } : feature
    )
  }));

  const savePage = async () => {
    if (!currentPage?.title?.trim() || !currentPage?.content?.trim() || !currentPage?.version?.trim()) {
      toast.error('Title, version, and content are required.');
      return;
    }
    setSaving(true);
    try {
      const response = await auth.fetch(`${config.API_BASE_URL}/api/admin/content/pages/${activeSection}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: currentPage.title,
          content: currentPage.content,
          type: currentPage.type || 'html',
          version: currentPage.version,
          effective_date: currentPage.effective_date || null,
          is_published: currentPage.is_published
        })
      });
      if (!response.ok) throw new Error(await errorMessage(response, 'Could not save the page.'));
      const body = await response.json();
      setPages(previous => ({
        ...previous,
        [activeSection]: { ...body.page, effective_date: toDateInput(body.page.effective_date), is_published: Boolean(body.page.is_published) }
      }));
      const names = {
        terms: 'Mobile Terms & Conditions',
        privacy: 'Mobile Privacy Policy',
        about: 'About HubScroll',
        business_terms: 'Business Terms of Service',
        business_privacy: 'Business Privacy Policy'
      };
      toast.success(`${names[activeSection]} updated.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const editFaq = (faq) => {
    setFaqForm({ ...faq, sort_order: Number(faq.sort_order) || 0, is_active: Boolean(faq.is_active) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveFaq = async (event) => {
    event.preventDefault();
    if (!faqForm.category.trim() || !faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Category, question, and answer are required.');
      return;
    }
    setSaving(true);
    try {
      const editing = Boolean(faqForm.id);
      const response = await auth.fetch(
        `${config.API_BASE_URL}/api/admin/content/faqs${editing ? `/${faqForm.id}` : ''}`,
        {
          method: editing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: faqForm.category,
            question: faqForm.question,
            answer: faqForm.answer,
            sort_order: Number(faqForm.sort_order) || 0,
            is_active: faqForm.is_active
          })
        }
      );
      if (!response.ok) throw new Error(await errorMessage(response, 'Could not save the FAQ.'));
      toast.success(`FAQ ${editing ? 'updated' : 'created'}.`);
      setFaqForm(emptyFaq);
      await loadContent();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const hideFaq = async (faq) => {
    if (!window.confirm(`Hide “${faq.question}” from the mobile help center?`)) return;
    try {
      const response = await auth.fetch(`${config.API_BASE_URL}/api/admin/content/faqs/${faq.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await errorMessage(response, 'Could not hide the FAQ.'));
      setFaqs(previous => previous.map(item => item.id === faq.id ? { ...item, is_active: false } : item));
      toast.success('FAQ hidden from the help center.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const categories = useMemo(() => [...new Set(faqs.map(faq => faq.category))].sort(), [faqs]);

  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = faqFilterCategory === 'all' || faq.category === faqFilterCategory;
      const matchesSearch = !faqSearchQuery.trim() ||
        faq.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [faqs, faqFilterCategory, faqSearchQuery]);

  // Render Realistic Smartphone Frame
  const renderDeviceMockup = (title, children) => {
    return (
      <div className="position-sticky" style={{ top: 24 }}>
        {/* Device Controls Bar */}
        <div className="d-flex align-items-center justify-content-between mb-12 px-2">
          <div className="d-flex align-items-center gap-2">
            <span className="w-8-px h-8-px rounded-circle bg-success-600 animate-pulse"></span>
            <span className="text-xs fw-bold text-dark text-uppercase letter-spacing-1">
              Mobile Preview (iPhone 16 Pro)
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-xs btn-outline-secondary radius-8 px-8 py-3 d-inline-flex align-items-center gap-1 text-xs shadow-none"
              onClick={() => setPreviewTheme(t => t === 'light' ? 'dark' : 'light')}
              title="Toggle Light / Dark Mode in Phone"
            >
              <Icon icon={previewTheme === 'light' ? "solar:moon-stars-bold" : "solar:sun-2-bold"} className="text-sm" />
              <span>{previewTheme === 'light' ? 'Dark' : 'Light'}</span>
            </button>
          </div>
        </div>

        {/* Realistic Smartphone Chassis */}
        <div className="hs-device-mockup">
          <div className={`hs-device-screen ${previewTheme === 'dark' ? 'is-dark' : ''}`}>
            {/* Dynamic Island & Status Bar */}
            <div className="hs-device-statusbar">
              <span>9:41</span>
              <div className="hs-device-island">
                <div className="hs-device-camera-lens"></div>
              </div>
              <div className="d-flex align-items-center gap-2 text-xs">
                <Icon icon="solar:round-transfer-vertical-bold" className="text-xs" />
                <Icon icon="solar:wifi-router-minimalistic-bold" className="text-xs" />
                <Icon icon="solar:battery-charge-bold" className="text-sm" />
              </div>
            </div>

            {/* In-App App Bar */}
            <div className="hs-device-appbar">
              <div className="d-flex align-items-center gap-1 text-primary-600 fw-semibold text-xs">
                <Icon icon="solar:alt-arrow-left-linear" className="text-sm" />
                <span>Profile</span>
              </div>
              <span className="fw-bold text-xs text-truncate text-center" style={{ maxWidth: 170 }}>
                {title}
              </span>
              <div className="text-secondary-light">
                <Icon icon="solar:share-linear" className="text-sm" />
              </div>
            </div>

            {/* Screen Scrollable Content */}
            <div className="hs-device-content">
              {children}
            </div>

            {/* Home Bar Indicator */}
            <div className="hs-device-homebar"></div>
          </div>
        </div>
      </div>
    );
  };

  const renderBusinessPortalMockup = (title, children) => {
    return (
      <div className="hs-device-wrapper">
        <div className="d-flex align-items-center justify-content-between mb-12 px-2">
          <div className="d-flex align-items-center gap-2">
            <span className="w-8-px h-8-px rounded-circle bg-primary-600 animate-pulse"></span>
            <span className="text-xs fw-bold text-dark text-uppercase letter-spacing-1">
              Business Portal Preview
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-xs btn-outline-secondary radius-8 px-8 py-3 d-inline-flex align-items-center gap-1 text-xs shadow-none"
              onClick={() => setPreviewTheme(t => t === 'light' ? 'dark' : 'light')}
              title="Toggle Light / Dark Mode"
            >
              <Icon icon={previewTheme === 'light' ? "solar:moon-stars-bold" : "solar:sun-2-bold"} className="text-sm" />
              <span>{previewTheme === 'light' ? 'Dark' : 'Light'}</span>
            </button>
          </div>
        </div>

        {/* Browser / Desktop Window Chassis */}
        <div className="card border radius-16 shadow-sm overflow-hidden bg-white" style={{ minHeight: '620px' }}>
          <div className="bg-neutral-100 px-16 py-10 border-bottom d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
              <span className="w-10-px h-10-px rounded-circle bg-danger-400 d-inline-block"></span>
              <span className="w-10-px h-10-px rounded-circle bg-warning-400 d-inline-block"></span>
              <span className="w-10-px h-10-px rounded-circle bg-success-400 d-inline-block"></span>
            </div>
            <div className="flex-grow-1 bg-white px-12 py-3 radius-6 text-xs text-secondary-light border d-flex align-items-center gap-2">
              <Icon icon="solar:lock-bold" className="text-success-600 text-xs" />
              <span>https://hubscroll.app/{activeSection === 'business_privacy' ? 'privacy' : 'tos'}</span>
            </div>
          </div>
          <div className={`p-24 overflow-y-auto ${previewTheme === 'dark' ? 'bg-neutral-900 text-white' : 'bg-white text-dark'}`} style={{ maxHeight: '620px' }}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  const renderAboutEditor = () => {
    if (!pages.about) {
      return (
        <div className="alert alert-warning radius-12 p-20 d-flex align-items-center gap-3">
          <Icon icon="solar:danger-triangle-bold" className="text-2xl text-warning-600" />
          <div>
            <div className="fw-bold">About HubScroll has not been initialized.</div>
            <div className="text-sm">Apply backend migration 032, then refresh.</div>
          </div>
        </div>
      );
    }

    const fields = [
      ['company', 'Company name'],
      ['location', 'Location'],
      ['founded', 'Founded'],
      ['website', 'Website'],
      ['email', 'Public email'],
      ['support_email', 'Support email']
    ];

    return (
      <div className="row gy-4">
        <div className="col-xl-7">
          {/* Card 1: App Overview */}
          <div className="card radius-16 border shadow-xs p-24 bg-base mb-24">
            <div className="d-flex justify-content-between align-items-center mb-20 pb-16 border-bottom">
              <div>
                <h6 className="fw-bold mb-1 text-dark">About the Application</h6>
                <p className="text-xs text-secondary-light mb-0">Core branding shown in mobile profile and store listings.</p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge radius-pill px-10 py-5 text-xs fw-semibold ${pages.about.is_published ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'}`}>
                  {pages.about.is_published ? 'Published' : 'Hidden'}
                </span>
                <div className="form-check form-switch mb-0">
                  <input
                    id="about-published"
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    checked={Boolean(pages.about.is_published)}
                    onChange={event => setPages(previous => ({ ...previous, about: { ...previous.about, is_published: event.target.checked } }))}
                  />
                </div>
              </div>
            </div>

            <div className="row gy-3">
              <div className="col-md-8">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  App Name
                </label>
                <input
                  className="form-control hs-input radius-8"
                  value={aboutData.app_name || ''}
                  onChange={event => updateAboutField('app_name', event.target.value)}
                  placeholder="e.g. HubScroll"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  App Version
                </label>
                <input
                  className="form-control hs-input radius-8"
                  value={aboutData.version || pages.about.version || ''}
                  onChange={event => {
                    const version = event.target.value;
                    updateAbout(current => ({ ...current, version }));
                    setPages(previous => ({ ...previous, about: { ...previous.about, version } }));
                  }}
                  placeholder="e.g. 1.0.0"
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  Tagline
                </label>
                <input
                  className="form-control hs-input radius-8"
                  value={aboutData.tagline || ''}
                  onChange={event => updateAboutField('tagline', event.target.value)}
                  placeholder="e.g. Discover the UAE's Premier Venues"
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  Description
                </label>
                <textarea
                  className="form-control radius-8"
                  rows="3"
                  value={aboutData.description || ''}
                  onChange={event => updateAboutField('description', event.target.value)}
                  placeholder="Brief introductory paragraph..."
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  Our Mission
                </label>
                <textarea
                  className="form-control radius-8"
                  rows="3"
                  value={aboutData.mission || ''}
                  onChange={event => updateAboutField('mission', event.target.value)}
                  placeholder="Our mission statement..."
                />
              </div>
            </div>
          </div>

          {/* Card 2: Contact Info */}
          <div className="card radius-16 border shadow-xs p-24 bg-base mb-24">
            <h6 className="fw-bold mb-1 text-dark">Company &amp; Contact Information</h6>
            <p className="text-xs text-secondary-light mb-20 pb-16 border-bottom">Official business credentials displayed for compliance.</p>
            <div className="row gy-3">
              {fields.map(([field, fieldLabel]) => (
                <div className="col-md-6" key={field}>
                  <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                    {fieldLabel}
                  </label>
                  <input
                    className="form-control hs-input radius-8"
                    value={aboutData[field] || ''}
                    onChange={event => updateAboutField(field, event.target.value)}
                  />
                </div>
              ))}
              <div className="col-md-6">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  Instagram URL
                </label>
                <input
                  className="form-control hs-input radius-8"
                  value={aboutData.social.instagram || ''}
                  onChange={event => updateAboutSocial('instagram', event.target.value)}
                  placeholder="https://instagram.com/hubscroll"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                  X / Twitter URL
                </label>
                <input
                  className="form-control hs-input radius-8"
                  value={aboutData.social.twitter || ''}
                  onChange={event => updateAboutSocial('twitter', event.target.value)}
                  placeholder="https://x.com/hubscroll"
                />
              </div>
            </div>
          </div>

          {/* Card 3: Feature Highlights */}
          <div className="card radius-16 border shadow-xs p-24 bg-base mb-24">
            <div className="d-flex justify-content-between align-items-center mb-20 pb-16 border-bottom">
              <div>
                <h6 className="fw-bold mb-1 text-dark">Mobile Feature Highlights</h6>
                <p className="text-xs text-secondary-light mb-0">Feature cards rendered in a 2-column grid inside the mobile app.</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm radius-8 d-flex align-items-center gap-2 fw-semibold"
                onClick={() => updateAbout(current => ({
                  ...current,
                  features: [...(current.features || []), { icon: '✨', title: 'New Feature', description: '' }]
                }))}
              >
                <Icon icon="solar:add-circle-bold" /> Add Feature Card
              </button>
            </div>

            {aboutData.features.length === 0 ? (
              <div className="text-center text-secondary-light py-4 border radius-12 bg-light">
                <Icon icon="solar:box-minimalistic-outline" className="text-3xl mb-2 opacity-50" />
                <p className="mb-0 text-sm">No feature highlights added yet.</p>
              </div>
            ) : (
              aboutData.features.map((feature, index) => (
                <div className="p-16 radius-12 border bg-light mb-16 transition-2" key={`${index}-${feature.title}`}>
                  <div className="row gy-2 align-items-start">
                    <div className="col-2">
                      <label className="form-label text-xs fw-semibold text-secondary-light mb-1">Emoji</label>
                      <input
                        className="form-control hs-input text-center radius-8 fs-5"
                        value={feature.icon || ''}
                        onChange={event => updateAboutFeature(index, 'icon', event.target.value)}
                      />
                    </div>
                    <div className="col-4">
                      <label className="form-label text-xs fw-semibold text-secondary-light mb-1">Feature Title</label>
                      <input
                        className="form-control hs-input radius-8"
                        value={feature.title || ''}
                        onChange={event => updateAboutFeature(index, 'title', event.target.value)}
                      />
                    </div>
                    <div className="col-5">
                      <label className="form-label text-xs fw-semibold text-secondary-light mb-1">Description</label>
                      <textarea
                        className="form-control radius-8"
                        rows="2"
                        value={feature.description || ''}
                        onChange={event => updateAboutFeature(index, 'description', event.target.value)}
                      />
                    </div>
                    <div className="col-1 pt-4 text-end">
                      <button
                        type="button"
                        className="w-32-px h-32-px rounded-circle bg-danger-50 text-danger-600 border-0 d-inline-flex align-items-center justify-content-center hover-bg-danger-100"
                        title="Delete feature"
                        onClick={() => updateAbout(current => ({
                          ...current,
                          features: (current.features || []).filter((_, featureIndex) => featureIndex !== index)
                        }))}
                      >
                        <Icon icon="solar:trash-bin-trash-bold" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            <div className="d-flex justify-content-end mt-20 pt-16 border-top">
              <button
                className="btn btn-primary px-24 py-10 radius-8 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                onClick={savePage}
                disabled={saving}
              >
                <Icon icon={saving ? "svg-spinners:180-ring-with-bg" : "solar:diskette-bold"} className="text-lg" />
                <span>{saving ? 'Saving…' : 'Save About Page'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Realistic iPhone Preview for About HubScroll */}
        <div className="col-xl-5">
          {renderDeviceMockup(
            aboutData.app_name || 'About HubScroll',
            <div className="py-2">
              <div className="text-center mb-3">
                <div
                  className="rounded-circle text-white d-inline-flex align-items-center justify-content-center fw-bold mb-2 shadow-sm"
                  style={{
                    width: 64,
                    height: 64,
                    fontSize: 26,
                    background: "linear-gradient(135deg, #0789d5 0%, #066fae 100%)"
                  }}
                >
                  {(aboutData.app_name || 'H').slice(0, 1)}
                </div>
                <h5 className="mb-0 fw-bold">{aboutData.app_name || 'HubScroll'}</h5>
                <p className="text-secondary-light text-xs mb-2">{aboutData.tagline || 'UAE Premier Venue Discovery'}</p>
                <span className="badge bg-neutral-200 text-neutral-700 radius-pill px-2 py-1 text-xs">
                  Version {aboutData.version || pages.about?.version || '1.0'}
                </span>
              </div>

              {aboutData.description && (
                <p className="text-xs text-secondary-light mb-3" style={{ lineHeight: 1.6 }}>
                  {aboutData.description}
                </p>
              )}

              {aboutData.mission && (
                <div className="bg-primary-50 border-start border-primary border-3 radius-8 p-12 mb-3">
                  <div className="text-primary-600 text-xs fw-bold mb-1 letter-spacing-1">OUR MISSION</div>
                  <div className="text-xs text-dark" style={{ lineHeight: 1.5 }}>{aboutData.mission}</div>
                </div>
              )}

              {aboutData.features && aboutData.features.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs fw-bold text-dark text-uppercase letter-spacing-1 mb-2">Key Features</div>
                  <div className="row g-2">
                    {aboutData.features.map((feature, index) => (
                      <div className="col-6" key={`preview-${index}`}>
                        <div className="border radius-8 p-2 h-100 bg-base shadow-xs">
                          <div className="text-xl mb-1">{feature.icon || "✨"}</div>
                          <div className="fw-semibold text-xs text-dark">{feature.title}</div>
                          <div className="text-xs text-secondary-light" style={{ fontSize: "11px", lineHeight: 1.3 }}>{feature.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-top pt-3 mt-3 text-xs text-secondary-light">
                <div className="fw-semibold text-dark mb-1">{aboutData.company || 'HubScroll Technologies LLC'}</div>
                {aboutData.location && <div className="mb-1"><Icon icon="solar:map-point-linear" className="me-1" />{aboutData.location}</div>}
                {aboutData.email && <div className="mb-1"><Icon icon="solar:letter-linear" className="me-1" />{aboutData.email}</div>}
                {aboutData.website && <div><Icon icon="solar:global-linear" className="me-1" />{aboutData.website}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Content & Policy Management" />

      {/* Main Container Card */}
      <div className="card radius-16 border shadow-xs overflow-hidden">
        {/* Top Header Banner */}
        <div className="p-24 border-bottom bg-base d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="w-48-px h-48-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-2xl flex-shrink-0 shadow-sm border border-primary-100">
              <Icon icon="solar:smartphone-update-bold" />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="mb-0 fw-bold text-dark">
                  {isBusinessSection ? 'Business Dashboard Policies & Legal' : 'Mobile App Content & Policies'}
                </h5>
                <span className="badge bg-success-50 text-success-600 border border-success-subtle radius-pill px-2 py-0 text-xs fw-semibold d-inline-flex align-items-center gap-1">
                  <span className="w-6-px h-6-px rounded-circle bg-success-600 animate-pulse"></span>
                  Live Sync Active
                </span>
              </div>
              <p className="text-secondary-light mb-0 text-sm">
                Changes are saved immediately and synced to the mobile app.
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-secondary radius-8 px-14 py-8 d-inline-flex align-items-center gap-2 fw-medium shadow-none hover-border-primary"
              onClick={loadContent}
              disabled={loading}
            >
              <Icon icon={loading ? "svg-spinners:180-ring-with-bg" : "solar:refresh-linear"} className="text-base" />
              <span>{loading ? "Refreshing..." : "Refresh Content"}</span>
            </button>
          </div>
        </div>

        {/* Body Container */}
        <div className="card-body p-24">
          {/* Top Level Category Pills: Mobile App vs Business Dashboard */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-20 pb-16 border-bottom">
            <div className="d-flex align-items-center gap-2 p-1 bg-neutral-100 radius-10">
              <button
                type="button"
                className={`btn btn-sm px-16 py-8 radius-8 fw-bold d-inline-flex align-items-center gap-2 transition-2 ${!isBusinessSection ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                onClick={() => {
                  if (isBusinessSection) setActiveSection('terms');
                }}
              >
                <Icon icon="solar:smartphone-bold" className="text-lg" />
                <span>Mobile App Content</span>
              </button>
              <button
                type="button"
                className={`btn btn-sm px-16 py-8 radius-8 fw-bold d-inline-flex align-items-center gap-2 transition-2 ${isBusinessSection ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                onClick={() => {
                  if (!isBusinessSection) setActiveSection('business_terms');
                }}
              >
                <Icon icon="solar:shop-2-bold" className="text-lg" />
                <span>Business Dashboard Policies</span>
              </button>
            </div>

            <span className="text-xs text-secondary-light fw-medium">
              {isBusinessSection ? 'Managing official policies for Business Owners' : 'Managing content & policies for Mobile App users'}
            </span>
          </div>

          {/* Sub-Navigation Pills for Selected Category */}
          <div className="hs-nav-pills d-flex flex-wrap gap-1 mb-24">
            {isBusinessSection ? (
              [
                ['business_terms', 'Business Terms of Service', 'solar:document-text-bold'],
                ['business_privacy', 'Business Privacy Policy', 'solar:shield-check-bold']
              ].map(([key, label, icon]) => (
                <button
                  key={key}
                  type="button"
                  className={`hs-nav-link ${activeSection === key ? 'active' : ''}`}
                  onClick={() => setActiveSection(key)}
                >
                  <Icon icon={icon} />
                  <span>{label}</span>
                </button>
              ))
            ) : (
              [
                ['terms', 'Terms & Conditions', 'solar:document-text-bold'],
                ['privacy', 'Privacy Policy', 'solar:shield-check-bold'],
                ['about', 'About HubScroll', 'solar:info-circle-bold'],
                ['faqs', 'Help Center FAQs', 'solar:question-circle-bold', faqs.length]
              ].map(([key, label, icon, count]) => (
                <button
                  key={key}
                  type="button"
                  className={`hs-nav-link ${activeSection === key ? 'active' : ''}`}
                  onClick={() => setActiveSection(key)}
                >
                  <Icon icon={icon} />
                  <span>{label}</span>
                  {count !== undefined && (
                    <span className={`badge ms-1 radius-pill px-2 py-0 text-xs ${activeSection === key ? 'bg-primary-50 text-primary-600' : 'bg-neutral-200 text-neutral-700'}`}>
                      {count}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-secondary-light mt-2 text-sm">Loading mobile content...</p>
            </div>
          ) : activeSection === 'about' ? (
            renderAboutEditor()
          ) : activeSection !== 'faqs' ? (
            currentPage ? (
              <div className="row gy-4">
                {/* Left Column: Modern Document Configuration & Rich Editor */}
                <div className="col-xl-7">
                  <div className="card radius-16 border shadow-xs p-24 bg-base">
                    <div className="d-flex align-items-center justify-content-between mb-20 pb-16 border-bottom">
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">Document Configuration</h6>
                        <p className="text-xs text-secondary-light mb-0">Set publication status, version number, and enforcement date.</p>
                      </div>
                      <span className={`badge radius-pill px-10 py-5 text-xs fw-semibold ${currentPage.is_published ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'}`}>
                        {currentPage.is_published ? (isBusinessSection ? 'Live in Business Portal' : 'Live in Mobile App') : 'Draft / Hidden'}
                      </span>
                    </div>

                    <div className="row gy-3 mb-24">
                      <div className="col-md-8">
                        <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                          Page Title
                        </label>
                        <input
                          className="form-control hs-input radius-8"
                          value={currentPage.title || ''}
                          onChange={event => updateCurrentPage('title', event.target.value)}
                          placeholder="e.g. Terms of Service"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                          Version
                        </label>
                        <input
                          className="form-control hs-input radius-8"
                          value={currentPage.version || ''}
                          onChange={event => updateCurrentPage('version', event.target.value)}
                          placeholder="e.g. 1.0"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                          Effective Date
                        </label>
                        <input
                          type="date"
                          className="form-control hs-input radius-8"
                          value={currentPage.effective_date || ''}
                          onChange={event => updateCurrentPage('effective_date', event.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                          {isBusinessSection ? 'Business Visibility' : 'Mobile Visibility'}
                        </label>
                        <div className={`p-10 radius-8 border d-flex align-items-center justify-content-between transition-2 ${currentPage.is_published ? 'bg-success-50 border-success-subtle' : 'bg-neutral-50 border-neutral-200'}`}>
                          <div>
                            <div className="fw-semibold text-xs d-flex align-items-center gap-1 text-dark">
                              <Icon icon={currentPage.is_published ? "solar:check-circle-bold" : "solar:eye-closed-linear"} className={currentPage.is_published ? "text-success-600" : "text-neutral-500"} />
                              {currentPage.is_published ? (isBusinessSection ? "Published in Portal" : "Published in App") : "Draft (Hidden)"}
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              id="content-published"
                              className="form-check-input cursor-pointer"
                              type="checkbox"
                              checked={Boolean(currentPage.is_published)}
                              onChange={event => updateCurrentPage('is_published', event.target.checked)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-24">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-0">
                          Document Content
                        </label>
                        <span className="text-xs text-secondary-light">Rich text with headers, lists &amp; links</span>
                      </div>
                      <div className="hs-modern-quill">
                        <ReactQuill
                          theme="snow"
                          modules={editorModules}
                          value={currentPage.content || ''}
                          onChange={value => updateCurrentPage('content', value)}
                          placeholder="Compose document content here..."
                        />
                      </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pt-16 border-top">
                      <div className="d-flex align-items-center gap-2 text-xs text-secondary-light">
                        <Icon icon="solar:info-circle-linear" className="text-base text-primary-600" />
                        <span>{isBusinessSection ? 'Saves immediately to database & updates Business Dashboard.' : 'Saves immediately to database & caches for mobile apps.'}</span>
                      </div>
                      <button
                        className="btn btn-primary px-24 py-10 radius-8 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                        onClick={savePage}
                        disabled={saving}
                      >
                        <Icon icon={saving ? "svg-spinners:180-ring-with-bg" : "solar:diskette-bold"} className="text-lg" />
                        <span>{saving ? 'Saving Changes…' : 'Save Changes'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column: Realistic iPhone Device Mockup or Business Portal Mockup */}
                <div className="col-xl-5">
                  {isBusinessSection ? (
                    renderBusinessPortalMockup(
                      currentPage.title || 'Business Document',
                      <div className="py-2">
                        <h5 className="fw-bold mb-2 text-dark">{currentPage.title || "Business Document"}</h5>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {currentPage.effective_date && (
                            <span className="badge bg-primary-50 text-primary-600 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:calendar-date-bold" className="text-xs" />
                              Effective {currentPage.effective_date}
                            </span>
                          )}
                          <span className="badge bg-neutral-200 text-neutral-700 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                            <Icon icon="solar:tag-bold" className="text-xs" />
                            v{currentPage.version || "1.0"}
                          </span>
                          {currentPage.is_published ? (
                            <span className="badge bg-success-50 text-success-600 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:check-circle-bold" className="text-xs" />
                              Live in Business Portal
                            </span>
                          ) : (
                            <span className="badge bg-warning-50 text-warning-700 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:eye-closed-bold" className="text-xs" />
                              Draft Mode
                            </span>
                          )}
                        </div>
                        <div
                          className="tos-content"
                          style={{ fontSize: "13px", lineHeight: "1.7" }}
                          dangerouslySetInnerHTML={{ __html: currentPage.content || '<p class="text-secondary-light">No content configured yet.</p>' }}
                        />
                      </div>
                    )
                  ) : (
                    renderDeviceMockup(
                      currentPage.title || 'Legal Document',
                      <div className="py-2">
                        <h5 className="fw-bold mb-2 text-dark">{currentPage.title || "Legal Document"}</h5>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {currentPage.effective_date && (
                            <span className="badge bg-primary-50 text-primary-600 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:calendar-date-bold" className="text-xs" />
                              Effective {currentPage.effective_date}
                            </span>
                          )}
                          <span className="badge bg-neutral-200 text-neutral-700 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                            <Icon icon="solar:tag-bold" className="text-xs" />
                            v{currentPage.version || "1.0"}
                          </span>
                          {currentPage.is_published ? (
                            <span className="badge bg-success-50 text-success-600 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:check-circle-bold" className="text-xs" />
                              Live in App
                            </span>
                          ) : (
                            <span className="badge bg-warning-50 text-warning-700 radius-pill px-2 py-1 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:eye-closed-bold" className="text-xs" />
                              Draft Mode
                            </span>
                          )}
                        </div>
                        <div
                          className="mobile-content-preview"
                          style={{ fontSize: "13px", lineHeight: "1.65" }}
                          dangerouslySetInnerHTML={{ __html: currentPage.content || '<p class="text-secondary-light">No content configured yet.</p>' }}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="alert alert-warning radius-12 p-20 d-flex align-items-center gap-3">
                <Icon icon="solar:danger-triangle-bold" className="text-2xl text-warning-600" />
                <div>
                  <div className="fw-bold">This page is missing.</div>
                  <div className="text-sm">Apply backend migration 031, then refresh.</div>
                </div>
              </div>
            )
          ) : (
            /* Help Center FAQs Section */
            <div className="row gy-4">
              {/* Left Column: Add / Edit FAQ Form */}
              <div className="col-xl-4">
                <form className="card radius-16 border shadow-xs p-24 bg-base position-sticky" style={{ top: 24 }} onSubmit={saveFaq}>
                  <div className="d-flex align-items-center justify-content-between mb-20 pb-16 border-bottom">
                    <h6 className="fw-bold mb-0 text-dark">
                      {faqForm.id ? 'Edit FAQ Item' : 'Add New FAQ Item'}
                    </h6>
                    {faqForm.id && (
                      <span className="badge bg-primary-100 text-primary-700 radius-pill px-2 py-1 text-xs">
                        Editing ID #{faqForm.id}
                      </span>
                    )}
                  </div>

                  <div className="mb-16">
                    <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                      Category
                    </label>
                    <input
                      className="form-control hs-input radius-8"
                      list="faq-categories"
                      value={faqForm.category}
                      onChange={event => setFaqForm(previous => ({ ...previous, category: event.target.value }))}
                      placeholder="e.g. Account, Reservations, Billing"
                    />
                    <datalist id="faq-categories">
                      {categories.map(category => <option value={category} key={category} />)}
                    </datalist>
                  </div>

                  <div className="mb-16">
                    <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                      Question
                    </label>
                    <textarea
                      className="form-control radius-8"
                      rows="3"
                      value={faqForm.question}
                      onChange={event => setFaqForm(previous => ({ ...previous, question: event.target.value }))}
                      placeholder="e.g. How do I make a reservation?"
                    />
                  </div>

                  <div className="mb-16">
                    <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                      Answer
                    </label>
                    <textarea
                      className="form-control radius-8"
                      rows="5"
                      value={faqForm.answer}
                      onChange={event => setFaqForm(previous => ({ ...previous, answer: event.target.value }))}
                      placeholder="Provide a clear, detailed answer..."
                    />
                  </div>

                  <div className="row gy-3 align-items-center mb-20">
                    <div className="col-6">
                      <label className="form-label fw-semibold text-xs text-uppercase letter-spacing-1 text-secondary-light mb-1">
                        Display Order
                      </label>
                      <input
                        type="number"
                        min="0"
                        className="form-control hs-input radius-8"
                        value={faqForm.sort_order}
                        onChange={event => setFaqForm(previous => ({ ...previous, sort_order: Number(event.target.value) }))}
                      />
                    </div>
                    <div className="col-6 pt-3">
                      <div className="form-check form-switch mb-0">
                        <input
                          id="faq-active"
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          checked={faqForm.is_active}
                          onChange={event => setFaqForm(previous => ({ ...previous, is_active: event.target.checked }))}
                        />
                        <label className="form-check-label fw-semibold text-sm ms-1" htmlFor="faq-active">
                          {faqForm.is_active ? 'Visible in App' : 'Hidden'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 pt-16 border-top">
                    <button className="btn btn-primary flex-grow-1 radius-8 fw-semibold" type="submit" disabled={saving}>
                      {saving ? 'Saving…' : faqForm.id ? 'Update FAQ' : 'Create FAQ'}
                    </button>
                    {faqForm.id && (
                      <button
                        className="hs-btn-secondary"
                        type="button"
                        onClick={() => setFaqForm(emptyFaq)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Right Column: FAQ Management Table & Category Filters */}
              <div className="col-xl-8">
                <div className="card radius-16 border shadow-xs overflow-hidden">
                  <div className="px-24 py-16 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3 bg-base">
                    <div>
                      <h6 className="mb-0 fw-bold text-dark">Mobile Help Center FAQs</h6>
                      <span className="text-xs text-secondary-light">
                        {faqs.filter(faq => Boolean(faq.is_active)).length} of {faqs.length} FAQs currently visible to mobile users
                      </span>
                    </div>

                    {/* Search & Category Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control hs-input radius-8 text-xs ps-32"
                          placeholder="Search questions..."
                          value={faqSearchQuery}
                          onChange={e => setFaqSearchQuery(e.target.value)}
                          style={{ width: 180 }}
                        />
                        <Icon icon="solar:magnifer-linear" className="position-absolute text-secondary-light top-50 start-0 translate-middle-y ms-10 text-sm" />
                      </div>
                      <select
                        className="form-select hs-select radius-8 text-xs py-6"
                        value={faqFilterCategory}
                        onChange={e => setFaqFilterCategory(e.target.value)}
                        style={{ width: 140 }}
                      >
                        <option value="all">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table bordered-table mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Order</th>
                          <th>Question &amp; Answer</th>
                          <th style={{ width: 110 }}>Status</th>
                          <th className="text-center" style={{ width: 100 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFaqs.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center text-secondary-light py-5">
                              <Icon icon="solar:clipboard-remove-outline" className="text-4xl opacity-50 mb-2" />
                              <p className="mb-0 text-sm">No FAQs match your search or filter.</p>
                            </td>
                          </tr>
                        ) : (
                          filteredFaqs.map(faq => (
                            <tr key={faq.id}>
                              <td>
                                <span className="badge bg-neutral-100 text-neutral-700 radius-pill px-2 py-1 text-xs fw-semibold">
                                  #{faq.sort_order}
                                </span>
                              </td>
                              <td style={{ minWidth: 320 }}>
                                <span className="badge bg-primary-50 text-primary-600 mb-2 radius-pill px-2 py-0 text-xs fw-semibold">
                                  {faq.category}
                                </span>
                                <div className="fw-bold text-dark text-sm">{faq.question}</div>
                                <div className="text-xs text-secondary-light mt-1" style={{ maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {faq.answer}
                                </div>
                              </td>
                              <td>
                                {Boolean(faq.is_active) ? (
                                  <span className="badge bg-success-50 text-success-600 border border-success-subtle radius-pill px-2 py-1 text-xs fw-semibold d-inline-flex align-items-center gap-1">
                                    <span className="w-6-px h-6-px rounded-circle bg-success-600"></span>
                                    Visible
                                  </span>
                                ) : (
                                  <span className="badge bg-neutral-200 text-neutral-700 radius-pill px-2 py-1 text-xs fw-semibold">
                                    Hidden
                                  </span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  <button
                                    className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 border-0 d-inline-flex align-items-center justify-content-center hover-bg-primary-100"
                                    onClick={() => editFaq(faq)}
                                    title="Edit FAQ"
                                  >
                                    <Icon icon="solar:pen-bold" className="text-sm" />
                                  </button>
                                  {Boolean(faq.is_active) && (
                                    <button
                                      className="w-32-px h-32-px rounded-circle bg-danger-50 text-danger-600 border-0 d-inline-flex align-items-center justify-content-center hover-bg-danger-100"
                                      onClick={() => hideFaq(faq)}
                                      title="Hide FAQ"
                                    >
                                      <Icon icon="solar:eye-closed-bold" className="text-sm" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default MobileContentPage;
